/**
 * Wording for the urgent risk alert record (TDI-701): the patient overview
 * flag and the proforma note. The rows come from rmsListUrgentRiskAlerts.
 *
 * Factual only: when the alert was sent, to which service, after whose
 * answer, the question and answer as the person saw them, and the answer now.
 * Nothing here rates an answer or prompts an action.
 *
 * Safeguarding wording is waiting on the accessibility and clinical check;
 * the sentences below are the ticket's, word for word.
 */

const SERVICE_NAMES = { adhd: 'ADHD', asd: 'autism' };

/** "ADHD service", "autism service" or "ADHD and autism services". */
export function serviceWords(services) {
  const names = ['adhd', 'asd']
    .filter((s) => Array.isArray(services) && services.includes(s))
    .map((s) => SERVICE_NAMES[s]);
  if (names.length === 0) return '';
  if (names.length === 1) return `${names[0]} service`;
  return `${names.join(' and ')} services`;
}

/** Who answered: the parent or carer, or the SENCO. */
export function respondentWords(role) {
  return role === 'school_user' ? 'SENCO' : 'parent or carer';
}

function toDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "1 November 2026", in UK time. */
export function alertDate(iso) {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/London',
  });
}

/** "14:05", in UK time. */
export function alertTime(iso) {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/London',
  });
}

// Ends a quoted question or answer with a full stop unless it already ends
// with one (a question mark), so "harm themselves?." never shows.
function sentence(text) {
  const value = String(text || '').trim();
  return /[.?!]$/.test(value) ? value : `${value}.`;
}

/**
 * The overview's sentences for one alert, in order:
 * the send, then "Answer now" if it changed, then the unsubmitted line.
 * An alert from before per-service inboxes has no services and leaves the
 * "to the ... service" part out rather than guessing.
 */
export function overviewAlertLines(alert) {
  if (!alert) return [];
  const services = serviceWords(alert.services);
  const to = services ? ` to the ${services}` : '';
  const form = alert.formTitle ? ` on ${alert.formTitle}` : '';
  const when = `on ${alertDate(alert.sentAt)} at ${alertTime(alert.sentAt)}, after the `
    + `${respondentWords(alert.respondentRole)} answered a question${form}. `
    + `Question: ${sentence(alert.questionText)} Answer given: ${sentence(alert.answerLabel)}`;
  // No inbox was set when it fired, so nothing was emailed: say so rather
  // than claim a send (wording not in the ticket; flagged for review).
  const lines = alert.delivered === false
    ? [`Not emailed: no inbox is set${services ? ` for the ${services}` : ''}. Recorded ${when}`]
    : [`Sent${to} ${when}`];
  if (alert.answerChanged) {
    lines.push(
      `Answer now: ${alert.currentAnswerLabel || 'No answer'} `
        + `(changed on ${alertDate(alert.answerChangedAt)}).`,
    );
  }
  if (!alert.formSubmitted) {
    lines.push("The form hasn't been submitted.");
  }
  return lines;
}

/** The proforma's note under the respondent's answer. */
export function proformaAlertNote(alert) {
  if (!alert) return '';
  const verb = alert.delivered === false ? 'not emailed (no inbox is set)' : 'sent';
  const sent = `Urgent risk alert ${verb} ${alertDate(alert.sentAt)} at ${alertTime(alert.sentAt)}.`;
  return alert.answerChanged ? `${sent} Answer given then: ${sentence(alert.answerLabel)}` : sent;
}

/**
 * The alerts for one respondent's answer cell: any of the cell's question
 * ids, answered by that respondent.
 */
export function alertsForAnswer(alerts, questionIds, role) {
  if (!Array.isArray(alerts) || alerts.length === 0) return [];
  const ids = new Set((questionIds || []).filter(Boolean));
  return alerts.filter((a) => ids.has(a.questionId) && (!role || a.respondentRole === role));
}
